import { useEffect } from "react";
import { useRouter } from "next/router";
import { captureException } from "@sentry/nextjs";
import Cookies from "js-cookie";
import shallow from "zustand/shallow";
import useUserStore from "stores/use-user-store";
import {
  shouldRedirectFromWatchlist,
  shouldRedirectToUpgrade,
} from "utils/auth-routing";
import getPayloadCookie from "utils/get-payload-cookie";
import {
  getUserDetails,
  postAuthRefresh,
  setUserLoggedIn,
  setUserLoggedOut,
} from "./utils";

/**
 * Handle routing based on user access level
 */
const handleUserRouting = (
  router: ReturnType<typeof useRouter>,
  userAccessGroup: string,
  directAccess: boolean
) => {
  const isFreeUser = userAccessGroup === "free";

  if (shouldRedirectToUpgrade(router.pathname, isFreeUser)) {
    router.push("/upgrade");
    return;
  }

  if (shouldRedirectFromWatchlist(router.pathname, directAccess)) {
    router.push("/");
  }
};

/**
 * Get the initial user data based on the auth token stored in a cookie and set the returned data in the user store
 */
const useGetInitialUserData = () => {
  const router = useRouter();
  const { updateUser } = useUserStore(
    (store) => ({
      updateUser: store.updateUser,
    }),
    shallow
  );

  useEffect(() => {
    /**
     * Get the initial user data based on the auth token stored in a cookie
     */
    const getInitialUserData = async (): Promise<void> => {
      try {
        if (Cookies.get("payload")) {
          setUserLoggedIn(updateUser);

          await postAuthRefresh();

          const detailsRes = await getUserDetails();
          const { directAccess } = getPayloadCookie().data;

          updateUser({
            isLoggedIn: true,
            loading: false,
            details: {
              id: detailsRes.id,
              username: detailsRes.username,
              intercom: detailsRes.intercom,
              firstName: detailsRes.firstName,
              lastName: detailsRes.lastName,
              directAccess,
              userAccessGroup: detailsRes.userAccessGroup,
              isTrialing: detailsRes.isTrialing,
            },
            access: {
              group: detailsRes.userAccessGroup,
              sessionObjective: detailsRes.userHasSessionObjective,
              permissions: detailsRes.userPermissions,
            },
          });

          handleUserRouting(router, detailsRes.userAccessGroup, directAccess);
        } else {
          throw Error("No cookie found");
        }
      } catch (error) {
        captureException(error);
        setUserLoggedOut(updateUser);
      }
    };

    getInitialUserData();
  }, [updateUser, router]);
};

export default useGetInitialUserData;
